import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/core';
import type { AutocompleteProvider, HandlebarsBlockType } from '../types';
import type { ProcessedAutocompleteItem } from '../autocomplete/core/types';
import { processProviderResponse, buildContext } from '../autocomplete/core/queryProcessor';
import { computeBlockContextAt } from '../extensions/handlebars/blockContext';

export interface BlockEditPopoverProps {
  initialValue: string;
  blockType?: HandlebarsBlockType;
  anchorRect: DOMRect;
  provider: AutocompleteProvider;
  editor: Editor;
  blockPos: number;
  showDescriptions?: boolean;
  allowFreeformHandlebars?: boolean;
  /** Called with the confirmed variable string */
  onConfirm: (variable: string) => void;
  onClose: () => void;
}

/**
 * Replace the last word in an expression with a new value.
 * e.g. replaceLastWord("eq fir", "firstName") → "eq firstName"
 * If the input ends with a space, appends instead.
 */
function replaceLastWord(input: string, replacement: string): string {
  if (!input || input.endsWith(' ')) {
    return input + replacement;
  }
  const lastSpaceIndex = input.lastIndexOf(' ');
  if (lastSpaceIndex === -1) {
    return replacement;
  }
  return input.slice(0, lastSpaceIndex + 1) + replacement;
}

export const BlockEditPopover: React.FC<BlockEditPopoverProps> = ({
  initialValue,
  blockType,
  anchorRect,
  provider,
  editor,
  blockPos,
  showDescriptions = false,
  allowFreeformHandlebars = false,
  onConfirm,
  onClose,
}) => {
  const [query, setQuery] = useState(initialValue);
  const [items, setItems] = useState<ProcessedAutocompleteItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Focus input on mount and select all text
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Query provider with only the current word (after last space)
  // so each word in the expression gets independent autocomplete
  const fetchSuggestions = useCallback(
    async (q: string) => {
      try {
        // blockPos is getPos() which is *before* the block node,
        // so resolving it gives us the enclosing blocks (parents only,
        // the block being edited is NOT included)
        const parentContext = computeBlockContextAt(editor, blockPos);

        // Extract just the current word for querying
        const lastSpaceIndex = q.lastIndexOf(' ');
        const currentWord = lastSpaceIndex === -1 ? q : q.slice(lastSpaceIndex + 1);

        const context = buildContext(currentWord, parentContext, editor);
        const result = await processProviderResponse(provider, currentWord, context, {
          showDescriptions,
          allowFreeformHandlebars,
        });
        // Filter out complete/freeform items — we only want real suggestions
        const suggestions = result.filter(
          (item) => !item.isComplete && item.id !== '__freeform__' && item.id !== '__freeform_block__' && item.id !== '__freeform_block_end__'
        );

        // In freeform mode, add a "use as typed" option when the query
        // doesn't exactly match a suggestion so the user knows they can
        // confirm any value.
        const trimmedQ = q.trim();
        if (
          allowFreeformHandlebars &&
          trimmedQ &&
          !suggestions.some((s) => !s.isError && s.value.toLowerCase() === trimmedQ.toLowerCase())
        ) {
          // Remove error/warning items — they're noise in freeform mode
          const realSuggestions = suggestions.filter((s) => !s.isError);
          realSuggestions.unshift({
            id: '__freeform_edit__',
            label: trimmedQ,
            value: trimmedQ,
            description: 'Use as entered',
            isComplete: false,
            isError: false,
          });
          setItems(realSuggestions);
        } else {
          setItems(suggestions);
        }
        setSelectedIndex(-1);
      } catch {
        setItems([]);
      }
    },
    [editor, blockPos, provider, showDescriptions, allowFreeformHandlebars]
  );

  // Fetch on mount with empty query to show all available options
  useEffect(() => {
    fetchSuggestions('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 150);
  };

  // Insert an autocomplete item into the input (replaces current word)
  const insertItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item || item.isError) return;

      // Freeform "use as entered" item — confirm directly
      if (item.id === '__freeform_edit__') {
        onConfirm(item.value);
        return;
      }

      const newQuery = replaceLastWord(query, item.value) + ' ';
      setQuery(newQuery);
      setSelectedIndex(-1);

      // Re-fetch suggestions for the updated input
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(newQuery);
      }, 150);

      // Keep focus on input
      inputRef.current?.focus();
    },
    [items, query, fetchSuggestions]
  );

  const handleConfirm = useCallback(() => {
    onConfirm(query.trim());
  }, [query, onConfirm]);

  const selectableItems = items.filter((item) => !item.isError);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (selectableItems.length === 0) return;
      setSelectedIndex((prev) => {
        if (prev < 0) return 0;
        return prev >= items.length - 1 ? 0 : prev + 1;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (selectableItems.length === 0) return;
      setSelectedIndex((prev) => {
        if (prev < 0) return items.length - 1;
        return prev <= 0 ? items.length - 1 : prev - 1;
      });
    } else if (e.key === 'Tab' && selectedIndex >= 0) {
      // Tab inserts the highlighted suggestion into the input
      e.preventDefault();
      insertItem(selectedIndex);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && items[selectedIndex] && !items[selectedIndex].isError) {
        // If a suggestion is highlighted, insert it into the input
        insertItem(selectedIndex);
      } else {
        // No suggestion highlighted: confirm the full input
        handleConfirm();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (!popoverRef.current || selectedIndex < 0) return;
    const selectedElement = popoverRef.current.querySelector(
      `[data-index="${selectedIndex}"]`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Position: below the anchor, clamped to viewport
  const position = {
    x: Math.min(anchorRect.left, window.innerWidth - 300),
    y: anchorRect.bottom + 4,
  };

  const popupHeight = Math.min(items.length * 48 + 40, 280);
  if (position.y + popupHeight > window.innerHeight) {
    position.y = anchorRect.top - popupHeight - 4;
  }

  return createPortal(
    <div
      ref={popoverRef}
      className="block-edit-popover"
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999,
      }}
    >
      <div className="block-edit-popover-header">
        <input
          ref={inputRef}
          type="text"
          className="block-edit-popover-input"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={blockType ? `Variable for #${blockType}...` : 'Variable...'}
          spellCheck={false}
          autoComplete="off"
        />
        <button
          type="button"
          className="block-edit-popover-confirm"
          onClick={handleConfirm}
          title="Confirm"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      </div>
      {items.length > 0 && (
        <div className="autocomplete-list">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              data-index={index}
              className={`autocomplete-item ${
                index === selectedIndex ? 'selected' : ''
              } ${item.isError ? 'error' : ''}`}
              onClick={() => !item.isError && insertItem(index)}
              disabled={item.isError}
            >
              <div className="autocomplete-item-content">
                <span className="autocomplete-item-label">{item.label}</span>
                {item.expandsTo && (
                  <span className="autocomplete-item-expansion">
                    → <code>{item.expandsTo}</code>
                  </span>
                )}
              </div>
              {item.description && (
                <span
                  className={
                    item.isError
                      ? 'autocomplete-error-message'
                      : 'autocomplete-item-description'
                  }
                >
                  {item.description}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

export default BlockEditPopover;
